import Component from '../component';
import Hex from '../hex';
import LinearGradient from '../linear-gradient';
import Stroke from '../stroke';

export function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

export function assertIsComponent(value: unknown): asserts value is Component {
	if (value instanceof Component) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of Component`);
}

export function assertIsNaNOrFinitePositiveInteger(value: unknown): asserts value is number {
	if (Number.isNaN(value)) {
		return;
	}
	if (typeof value === 'number' && Number.isInteger(value) && value > 0 && Number.isFinite(value)) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be Number.NaN or a finite positive Integer`);
}

export function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

export function assertIsNonNegativeOrAuto(value: unknown): asserts value is 'auto' | number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	if (value === 'auto') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a non negative number or "auto"`);
}

export function assertIsNotChild(child: unknown, parent: unknown): void {
	if (parent instanceof Node && child instanceof Node && parent.contains(child) === false) {
		return;
	}
	throw new TypeError(`[${String(child)}] is already a child of ${String(parent)}`);
}

export function assertIsTwoItemTuple(value: unknown): asserts value is [number, number] {
	if (Array.isArray(value) && value.length === 2) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a tuple with 2 items`);
}

export function assertIsValidAlignment(value: unknown): asserts value is 'bottom_center' | 'bottom_left' | 'bottom_right' | 'center' | 'left' | 'right' | 'top_center' | 'top_left' | 'top_right' {
	if (isTop(value) || isMiddle(value) || isBottom(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "top_left", "top_center", "top_right", "left", "center", "right", "bottom_left", "bottom_center" or "bottom_right"`);
}

export function assertIsValidAutoLayout(value: unknown): asserts value is 'grid' | 'horizontal' | 'vertical' | 'wrap' {
	if (value === 'grid' || value === 'horizontal' || value === 'vertical' || value === 'wrap') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "grid", "horizontal", "vertical" or "wrap"`);
}

export function assertIsValidCornerRadius(value: unknown): asserts value is [number, number, number, number] | number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	assertIsFourItemTuple(value);
	assertTupleItemsAreNumbers(value);
	assertTupleItemsAreNonNegative(value);
}

export function assertIsValidFill(value: unknown): asserts value is Hex | LinearGradient | null {
	if (value instanceof Hex || value instanceof LinearGradient || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be instance of Hex / LinearGradient or null`);
}

export function assertIsValidPaddingHorizontalOrVertical(value: unknown): asserts value is [number, number] | number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	assertIsTwoItemTuple(value);
	assertTwoItemsTupleAreNumbers(value);
	assertTwoItemsTupleAreNonNegative(value);
}

export function assertIsValidPaddingSide(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a non negative number`);
}

export function assertIsValidStroke(value: unknown): asserts value is null | Stroke {
	if (value instanceof Stroke || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be instance of Stroke or null`);
}

export function assertIsValidVisible(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be boolean`);
}

export function assertTwoItemsTupleAreNonNegative(value: [number, number]): asserts value is [number, number] {
	if (value[0] >= 0 && value[1] >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be tuple with 2 non negative number items`);
}

function assertIsFourItemTuple(value: unknown): asserts value is [unknown, unknown, unknown, unknown] {
	if (Array.isArray(value) && value.length === 4) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a tuple with 4 items`);
}

function assertTupleItemsAreNonNegative(value: [number, number, number, number]): asserts value is [number, number, number, number] {
	const [topLeft, topRight, bottomLeft, bottomRight] = value;
	if (topLeft >= 0 && topRight >= 0 && bottomLeft >= 0 && bottomRight >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be tuple with 4 non negative number items`);
}

function assertTupleItemsAreNumbers(value: [unknown, unknown, unknown, unknown]): asserts value is [number, number, number, number] {
	const [topLeft, topRight, bottomLeft, bottomRight] = value;
	if (typeof topLeft === 'number' && typeof topRight === 'number' && typeof bottomLeft === 'number' && typeof bottomRight === 'number') {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be tuple with 4 number items`);
}

function assertTwoItemsTupleAreNumbers(value: [unknown, unknown]): asserts value is [number, number] {
	const [left, right] = value;
	if (typeof left === 'number' && typeof right === 'number') {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be tuple with 2 number items`);
}

function isBottom(value: unknown): boolean {
	return value === 'bottom_left' || value === 'bottom_center' || value === 'bottom_right';
}

function isMiddle(value: unknown): boolean {
	return value === 'left' || value === 'center' || value === 'right';
}

function isTop(value: unknown): boolean {
	return value === 'top_left' || value === 'top_center' || value === 'top_right';
}

export function assertIsChild(child: unknown, parent: unknown): void {
	if (parent instanceof Node && child instanceof Node && child.parentNode === parent) {
		return;
	}
	throw new TypeError(`[${String(child)}] is not a child of ${String(parent)}`);
}
